import React, { useEffect, useRef, useState } from 'react';
import { loadStripe, type Stripe, type StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { X, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { track } from '../lib/th';

// Inline subscribe flow: clicking a plan opens ONE dialog that gates on the
// server session — an unauthenticated user signs in right inside the dialog,
// then the embedded Stripe checkout mounts in place without ever leaving the
// page. Mirrors the netwrck/openpaths single-dialog subscribe flow.

const stripeCache = new Map<string, Promise<Stripe | null>>();
function getStripe(key: string): Promise<Stripe | null> {
  let p = stripeCache.get(key);
  if (!p) {
    p = loadStripe(key);
    stripeCache.set(key, p);
  }
  return p;
}

function friendlyAuthError(err: unknown): string {
  const code = (err as { code?: string })?.code || '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks invalid.';
    case 'auth/missing-password':
      return 'Enter your password.';
    case 'auth/weak-password':
      return 'Choose a password with at least 6 characters.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'legacy/invalid-credentials':
      return 'Invalid email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again in a moment.';
    case 'auth/popup-blocked':
    case 'auth/account-exists-with-different-credential':
      return (err as Error).message;
    default:
      return (err as Error)?.message || 'Sign in failed';
  }
}

// Compact email (auto-flip login→register) + Google + GitHub sign-in. Unlike the
// full-page LoginPanel it calls onSuccess() instead of navigating, so the caller
// can continue the in-place checkout once the session cookie is set.
const InlineAuthForm: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { signInWithEmail, signUpWithEmail, signInWithGoogle, signInWithGithub } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'' | 'email' | 'google' | 'github'>('');
  const disabled = busy !== '';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy('email');
    setError('');
    try {
      if (mode === 'login') {
        try {
          await signInWithEmail(email, password);
        } catch (err) {
          const code = (err as { code?: string })?.code || '';
          if (!['auth/user-not-found', 'auth/invalid-credential', 'legacy/invalid-credentials'].includes(code)) throw err;
          try {
            await signUpWithEmail(email, password);
          } catch (registerErr) {
            const rc = (registerErr as { code?: string })?.code || '';
            if (rc === 'auth/email-already-in-use' || rc === 'legacy/email-already-in-use') {
              throw Object.assign(new Error('Invalid email or password.'), { code: 'legacy/invalid-credentials' });
            }
            throw registerErr;
          }
        }
      } else {
        try {
          await signUpWithEmail(email, password);
        } catch (err) {
          const code = (err as { code?: string })?.code || '';
          if (code !== 'auth/email-already-in-use' && code !== 'legacy/email-already-in-use') throw err;
          // Existing account — sign them straight in with the same password.
          try {
            await signInWithEmail(email, password);
          } catch (signInErr) {
            const sc = (signInErr as { code?: string })?.code || '';
            if (['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential', 'legacy/invalid-credentials'].includes(sc)) {
              setMode('login');
              setError('You already have an account. Enter your password to sign in.');
              return;
            }
            throw signInErr;
          }
        }
      }
      onSuccess();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy('');
    }
  }

  async function oauth(provider: 'google' | 'github') {
    setBusy(provider);
    setError('');
    try {
      if (provider === 'google') await signInWithGoogle();
      else await signInWithGithub();
      onSuccess();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy('');
    }
  }

  return (
    <form onSubmit={submit} data-testid="subscribe-auth">
      <p className="mb-4 text-sm text-white/56">Sign in or create an account to continue to checkout.</p>
      <div className="mb-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => { setMode('login'); setError(''); }}
          aria-pressed={mode === 'login'}
          className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-bold transition-all ${mode === 'login' ? 'border-white bg-sky-400/15 text-sky-200 shadow-[0_0_18px_rgba(255,255,255,0.25)]' : 'border-white/15 bg-white/[0.02] text-white/55 hover:border-white/35 hover:text-white'}`}
        >
          <LogIn size={16} /> Sign in
        </button>
        <button
          type="button"
          onClick={() => { setMode('register'); setError(''); }}
          aria-pressed={mode === 'register'}
          className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-bold transition-all ${mode === 'register' ? 'border-white bg-sky-400/15 text-sky-200 shadow-[0_0_18px_rgba(255,255,255,0.25)]' : 'border-white/15 bg-white/[0.02] text-white/55 hover:border-white/35 hover:text-white'}`}
        >
          <UserPlus size={16} /> Create account
        </button>
      </div>

      <label className="mb-1.5 block text-[11px] uppercase tracking-widest text-white/55" htmlFor="subscribe-email">Email</label>
      <input
        id="subscribe-email"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mb-3 h-10 w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 text-sm text-white outline-none transition-colors focus:border-white focus:bg-white/10"
      />
      <label className="mb-1.5 block text-[11px] uppercase tracking-widest text-white/55" htmlFor="subscribe-password">Password</label>
      <input
        id="subscribe-password"
        type="password"
        required
        minLength={6}
        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="mb-4 h-10 w-full rounded-lg border border-white/15 bg-white/[0.03] px-3 text-sm text-white outline-none transition-colors focus:border-white focus:bg-white/10"
      />

      {error && <p className="mb-3 rounded-lg border border-red-500/40 bg-red-950/60 px-3 py-2 text-sm text-red-100">{error}</p>}

      <button
        type="submit"
        disabled={disabled}
        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-sky-400 px-5 text-sm font-bold text-black transition-colors hover:bg-sky-300 disabled:opacity-60"
      >
        {mode === 'login' ? <LogIn size={18} /> : <UserPlus size={18} />}
        {busy === 'email' ? 'Working…' : mode === 'login' ? 'Continue' : 'Create account'}
      </button>

      <div className="my-4 flex items-center gap-3 text-[11px] uppercase tracking-widest text-white/35">
        <span className="h-px flex-1 bg-white/15" />
        or
        <span className="h-px flex-1 bg-white/15" />
      </div>

      <div className="grid gap-2.5">
        <button
          type="button"
          onClick={() => oauth('google')}
          disabled={disabled}
          className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-lg border border-white/20 bg-white/[0.03] px-4 text-sm font-bold text-white transition-colors hover:border-white/40 hover:bg-white/10 disabled:opacity-60"
        >
          <GoogleIcon />
          {busy === 'google' ? 'Connecting…' : 'Continue with Google'}
        </button>
        <button
          type="button"
          onClick={() => oauth('github')}
          disabled={disabled}
          className="inline-flex h-10 w-full items-center justify-center gap-2.5 rounded-lg border border-white/20 bg-white/[0.03] px-4 text-sm font-bold text-white transition-colors hover:border-white/40 hover:bg-white/10 disabled:opacity-60"
        >
          <GitHubIcon />
          {busy === 'github' ? 'Connecting…' : 'Continue with GitHub'}
        </button>
      </div>
    </form>
  );
};

type Stage = 'closed' | 'auth' | 'loading' | 'checkout' | 'error';

export interface SubscribeFlow {
  // open the dialog for a plan ('free' short-circuits to /signup).
  start: (plan: string) => void;
  // the plan whose button should show a busy label, '' when idle.
  pendingPlan: string;
  // last error, surfaced by the caller if it wants an inline banner too.
  error: string;
  // the dialog element — render once, anywhere in the tree.
  element: React.ReactNode;
}

// useSubscribeFlow drives the gated dialog. Auth is gated on the SERVER session
// (a 401 from /api/plans/subscribe), not client-side Firebase state, so it works
// identically with Firebase and the cookie-only legacy/e2e path.
export function useSubscribeFlow(publishableKey?: string, surface = 'pricing_page'): SubscribeFlow {
  const [stage, setStage] = useState<Stage>('closed');
  const [plan, setPlan] = useState('');
  const [error, setError] = useState('');
  const mountRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<StripeEmbeddedCheckout | null>(null);
  const publishableKeyRef = useRef(publishableKey);

  useEffect(() => () => { instanceRef.current?.destroy(); }, []);
  useEffect(() => { publishableKeyRef.current = publishableKey; }, [publishableKey]);
  useEffect(() => {
    if (stage === 'checkout' && mountRef.current && instanceRef.current) {
      instanceRef.current.mount(mountRef.current);
    }
  }, [stage]);

  function close() {
    instanceRef.current?.destroy();
    instanceRef.current = null;
    setStage('closed');
    setPlan('');
    setError('');
  }

  async function beginCheckout(planId: string) {
    setError('');
    setStage('loading');
    let clientSecret: string;
    try {
      const res = await fetch('/api/plans/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId }),
      });
      if (res.status === 401) {
        track('paywall_signup_clicked', { surface, plan: planId });
        setStage('auth');
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.clientSecret) throw new Error(data.error || 'Could not start checkout');
      clientSecret = data.clientSecret as string;
      track('begin_checkout', { plan: planId, surface });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout');
      setStage('error');
      return;
    }

    try {
      // A user can click before the pricing catalogue request has hydrated the
      // key. Resolve it here as well so an early click still reaches Stripe.
      let key = publishableKeyRef.current;
      if (!key) {
        const config = await fetch('/api/plans').then((response) => (response.ok ? response.json() : null));
        key = config?.publishableKey;
        if (key) publishableKeyRef.current = key;
      }
      if (!key) throw new Error('Checkout is not configured yet.');
      const stripe = await getStripe(key);
      if (!stripe) throw new Error('Stripe failed to load');
      instanceRef.current?.destroy();
      instanceRef.current = null;
      const checkout = await stripe.createEmbeddedCheckoutPage({ clientSecret });
      instanceRef.current = checkout;
      setStage('checkout');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout');
      setStage('error');
    }
  }

  function start(planId: string) {
    if (planId === 'free') {
      window.location.href = '/signup';
      return;
    }
    setPlan(planId);
    beginCheckout(planId);
  }

  const element =
    stage === 'closed' ? null : (
      <div
        className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/75 p-4 backdrop-blur-sm sm:p-8"
        role="dialog"
        aria-modal="true"
        aria-label="Subscribe"
        data-testid="subscribe-dialog"
        onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      >
        <div className="my-auto w-full max-w-xl rounded-2xl border border-white/12 bg-[#0b0b0c] p-5 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold text-white">{stage === 'auth' ? 'Sign in to subscribe' : `Secure ${plan ? `${plan[0].toUpperCase()}${plan.slice(1)} ` : ''}checkout`}</h3>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="rounded-md p-1 text-white/56 transition-colors hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          {error && (
            <p className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>
          )}

          {stage === 'auth' && <InlineAuthForm onSuccess={() => beginCheckout(plan)} />}

          {stage === 'loading' && !error && (
            <div className="flex items-center justify-center gap-3 py-12 text-sm text-white/56" data-testid="subscribe-loading">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Starting secure checkout…
            </div>
          )}

          {stage === 'error' && (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="inline-flex h-10 items-center justify-center rounded-lg border border-white/15 px-4 text-sm font-bold text-white/70 hover:border-white/35 hover:text-white"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => beginCheckout(plan)}
                className="inline-flex h-10 items-center justify-center rounded-lg bg-sky-400 px-4 text-sm font-bold text-black hover:bg-sky-300"
              >
                Try again
              </button>
            </div>
          )}

          <div ref={mountRef} id="subscribe-checkout-mount" className={stage === 'checkout' ? '' : 'hidden'} />
        </div>
      </div>
    );

  return { start, pendingPlan: stage === 'loading' ? plan : '', error, element };
}

const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.52 12.27c0-.82-.07-1.6-.2-2.36H12v4.48h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.75z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.72-2.45 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.26v3.09A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56V6.63H1.26a12 12 0 0 0 0 10.74l4.01-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.43-3.43A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.26 6.63l4.01 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const GitHubIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 .5C5.37.5 0 5.87 0 12.5c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58v-2.03c-3.34.73-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.2.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5.99.11-.78.42-1.31.76-1.61-2.67-.3-5.47-1.34-5.47-5.95 0-1.31.47-2.39 1.24-3.23-.13-.31-.54-1.53.12-3.19 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.29-1.55 3.3-1.23 3.3-1.23.66 1.66.25 2.88.12 3.19.77.84 1.24 1.92 1.24 3.23 0 4.62-2.81 5.64-5.49 5.94.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.83.58A12 12 0 0 0 24 12.5C24 5.87 18.63.5 12 .5z" />
  </svg>
);
